import React, { useState, useEffect } from 'react'
import {
  Modal,
  Calendar,
  Card,
  Space,
  Button,
  Tag,
  Typography,
  Spin,
  Alert,
  message,
  Row,
  Col,
  Badge,
  Empty
} from 'antd'
import {
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CheckCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { customerApi } from '../services/customerApi'

const { Title, Text } = Typography

interface AppointmentSchedulerProps {
  visible: boolean
  estimateId: string
  estimateTitle: string
  token?: string
  depositRequired?: boolean
  invoiceNumber?: string
  onClose: () => void
  onSuccess: (appointment: any) => void
}

interface TimeSlot {
  startTime: string
  endTime: string
  duration: number
  technicianId: string
  technicianName: string
  date: string
}

export const AppointmentScheduler: React.FC<AppointmentSchedulerProps> = ({
  visible,
  estimateId,
  estimateTitle,
  token,
  depositRequired,
  invoiceNumber,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [scheduling, setScheduling] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null)
  const [availableSlots, setAvailableSlots] = useState<Record<string, TimeSlot[]>>({})
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null)

  useEffect(() => {
    if (visible) {
      loadAvailableSlots()
    }
  }, [visible])

  const loadAvailableSlots = async () => {
    try {
      setLoading(true)
      const startDate = dayjs().format('YYYY-MM-DD')
      const endDate = dayjs().add(14, 'days').format('YYYY-MM-DD')

      console.log('🔍 [AppointmentScheduler] Loading slots for:', { estimateId, startDate, endDate })

      const response = await customerApi.getAvailableSlots(
        estimateId,
        {
          startDate,
          endDate,
          duration: 120 // 2 hours default
        },
        token
      )

      console.log('✅ [AppointmentScheduler] Received response:', response)
      console.log('✅ [AppointmentScheduler] Total slots:', response.totalSlots)
      console.log('✅ [AppointmentScheduler] Slots by date:', response.slots)

      if (response.success) {
        setAvailableSlots(response.slots || {})
      } else {
        message.error('Failed to load available slots')
      }
    } catch (error: any) {
      console.error('❌ [AppointmentScheduler] Failed to load slots:', error)
      message.error(error.response?.data?.message || 'Failed to load available appointment slots')
    } finally {
      setLoading(false)
    }
  }

  const handleDateSelect = (date: Dayjs) => {
    setSelectedDate(date)
    setSelectedSlot(null)
  }

  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot)
  }

  const handleSchedule = async () => {
    if (!selectedSlot) {
      message.error('Please select a time slot')
      return
    }

    try {
      setScheduling(true)
      const response = await customerApi.scheduleAppointment(
        estimateId,
        {
          appointmentTime: selectedSlot.startTime,
          technicianId: selectedSlot.technicianId,
          duration: selectedSlot.duration,
          notes: `Scheduled from estimate approval`
        },
        token
      )

      if (response.success) {
        message.success('Appointment scheduled successfully!')
        onSuccess(response.job)
      } else {
        message.error('Failed to schedule appointment')
      }
    } catch (error: any) {
      console.error('Failed to schedule:', error)
      message.error(error.response?.data?.message || 'Failed to schedule appointment')
    } finally {
      setScheduling(false)
    }
  }

  const cellRender = (date: Dayjs) => {
    const dateKey = date.format('YYYY-MM-DD')
    const slots = availableSlots[dateKey] || []
    
    if (slots.length > 0) {
      return (
        <Badge count={slots.length} style={{ backgroundColor: '#52c41a' }} />
      )
    }
    return null
  }

  const selectedDateSlots = selectedDate 
    ? availableSlots[selectedDate.format('YYYY-MM-DD')] || []
    : []

  const disabledDate = (date: Dayjs) => {
    const dateKey = date.format('YYYY-MM-DD')
    return !availableSlots[dateKey] || availableSlots[dateKey].length === 0
  }

  return (
    <Modal
      open={visible}
      title={
        <Space>
          <CalendarOutlined />
          <span>Schedule Your Appointment</span>
        </Space>
      }
      onCancel={onClose}
      width={900}
      footer={null}
      destroyOnClose
    >
      {depositRequired && invoiceNumber && (
        <Alert
          message="Deposit Required"
          description={
            <div>
              <p>A deposit invoice ({invoiceNumber}) has been created and sent to your email.</p>
              <p>You can schedule your appointment now, and payment can be made before the scheduled date.</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>{estimateTitle}</span>
          </Space>
        }
        style={{ marginBottom: '24px' }}
      >
        <Text type="secondary">
          Select a date and time for your appointment. Available slots are shown in green on the calendar.
        </Text>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>Loading available appointments...</p>
        </div>
      ) : (
        <Row gutter={24}>
          <Col xs={24} md={14}>
            <Card title="Select a Date" size="small">
              <Calendar
                fullscreen={false}
                value={selectedDate || undefined}
                onSelect={handleDateSelect}
                cellRender={cellRender}
                disabledDate={disabledDate}
                validRange={[dayjs(), dayjs().add(14, 'days')]}
              />
            </Card>
          </Col>

          <Col xs={24} md={10}>
            <Card 
              title={
                selectedDate 
                  ? `Available Times - ${selectedDate.format('MMM D, YYYY')}`
                  : 'Select a Date'
              }
              style={{ height: '100%', minHeight: '400px' }}
              size="small"
            >
              {!selectedDate ? (
                <Empty
                  description="Please select a date from the calendar"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : selectedDateSlots.length === 0 ? (
                <Empty
                  description="No available slots for this date"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {selectedDateSlots.map((slot, index) => {
                    const startTime = dayjs(slot.startTime)
                    const endTime = dayjs(slot.endTime)
                    const isSelected = selectedSlot === slot

                    return (
                      <Card
                        key={index}
                        size="small"
                        hoverable
                        style={{
                          border: isSelected ? '2px solid #52c41a' : undefined,
                          backgroundColor: isSelected ? '#f6ffed' : undefined,
                          cursor: 'pointer'
                        }}
                        onClick={() => handleSlotSelect(slot)}
                      >
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          <Space>
                            <ClockCircleOutlined style={{ color: '#1890ff' }} />
                            <Text strong>
                              {startTime.format('h:mm A')} - {endTime.format('h:mm A')}
                            </Text>
                            {isSelected && (
                              <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            )}
                          </Space>
                          <Space size={4}>
                            <UserOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {slot.technicianName}
                            </Text>
                          </Space>
                          <Tag color="blue" style={{ marginTop: '4px' }}>
                            {slot.duration} minutes
                          </Tag>
                        </Space>
                      </Card>
                    )
                  })}
                </Space>
              )}
            </Card>
          </Col>
        </Row>
      )}

      <div style={{ marginTop: '24px', textAlign: 'right' }}>
        <Space>
          <Button onClick={onClose} size="large">
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<CalendarOutlined />}
            onClick={handleSchedule}
            loading={scheduling}
            disabled={!selectedSlot}
          >
            Schedule Appointment
          </Button>
        </Space>
      </div>
    </Modal>
  )
}

